import { Box, visuallyHidden } from "@outschool/backpack";
import {
  IconDefinition,
  faCalendarAlt,
  faChalkboardTeacher,
  faChartLine,
  faChevronDown,
  faChevronUp,
  faHeart,
  faQuestionCircle,
  faSearch,
  faSignIn,
  faUsersCog
} from "@outschool/icons";
import {
  BASE_LOCALE,
  TFunction,
  useLocale,
  useTranslation
} from "@outschool/localization";
import {
  leadActivitiesPath,
  searchPath,
  sellerOrgPath,
  teacherDashboardPath,
  teacherSchedulePath
} from "@outschool/routes";
import { useSession } from "@outschool/ui-auth";
import { UserHeadshotImage } from "@outschool/ui-components-shared";
import { NavHelpLink } from "@outschool/ui-components-website";
import {
  Icon,
  Popover,
  Space,
  useImpressionTracking
} from "@outschool/ui-legacy-component-library";
import { SystemStyleObject } from "@styled-system/css";
import React from "react";

import { FeatureFlag } from "../../../shared/Env";
import * as Routes from "../../../shared/Routes";
import { useHasEnrolled, usePricingOfferAdmin } from "../../hooks/CurrentUser";
import { isBuyerOrgAdmin } from "../../lib/BuyerOrgAdmins";
import { useFeatureFlag } from "../../lib/FeatureFlags";
import useEventListener, {
  useClickOutsideRefsEventListener
} from "../../lib/useEventListener";
import { UserMode, useUserMode } from "../../lib/UserMode";
import JoinAndWelcomeButton from "../JoinAndWelcomeButton";
import Link from "../Link";
import HeaderMenu from "./HeaderMenu";
import HeaderNavItem from "./HeaderNavItem";
import { EducatorModeToolbar } from "./TeachToolbar";
import WalletFundsNavItem from "./WalletFundsNavItem";

export default function DesktopHeaderNav() {
  const { isLoggedIn, isLeader, currentUser } = useSession();

  // Show Manage link for seller org owners
  const isSellerOrg = !!currentUser?.sellerOrg?.currentUserIsOwner;

  const showWalletFunds = isLoggedIn && !!currentUser?.pricingOffer;

  // If user is buyer org admin and FF enabled, hide all nav items
  const buyerOrgAdminModeEnabled = useFeatureFlag(
    FeatureFlag.BuyerOrgAdminMode
  );
  const isBuyerOrgAdminMode =
    buyerOrgAdminModeEnabled && isBuyerOrgAdmin(currentUser);

  const { t } = useTranslation("client\\components\\nav\\DesktopHeaderNav");
  const innerProps = {
    isLeader,
    isSellerOrg,
    isOrgApplicant: !!currentUser?.latestSellerOrgApplication,
    showWalletFunds,
    isBuyerOrgAdminMode,
    t
  };
  if (isLoggedIn) {
    return (
      <Box sx={{ display: "flex" }} data-test-id="logged-in-nav">
        <LoggedInNav {...innerProps} />
      </Box>
    );
  } else {
    return <AnonymousNav t={t} />;
  }
}

/** Header navigation for non-logged in users */
function AnonymousNav({ t }: { t: TFunction }) {
  return (
    <NavItems>
      <FindClassesLink t={t} />
      <HelpLink t={t} hideLabel={false} />
      <LoginLink t={t} />
      <Space x="small" />
      <JoinAndWelcomeButton>{t`Join`}</JoinAndWelcomeButton>
      <Space x="xsmall" />
    </NavItems>
  );
}

/** Header navigation for logged in users, when the educator mode feature flag is off */
function LoggedInNav({
  isLeader,
  isSellerOrg,
  isOrgApplicant,
  showWalletFunds,
  isBuyerOrgAdminMode,
  t
}) {
  // For parents, always show Schedule link
  // For teachers, query if they have enrolled
  const showScheduleLink = useHasEnrolled({ skip: !isLeader });
  const pricingOfferAdminOrgUid = usePricingOfferAdmin();

  if (!isSellerOrg && !isBuyerOrgAdminMode) {
    return (
      <UserModeNav
        isLeader={isLeader}
        isSellerOrg={isSellerOrg}
        showScheduleLink={showScheduleLink}
        pricingOfferAdminOrgUid={pricingOfferAdminOrgUid}
        showWalletFunds={showWalletFunds}
        t={t}
      />
    );
  } else if (isBuyerOrgAdminMode) {
    // The empty box is used to ensure the menu popover properly handles overflow
    return (
      <Box
        flex
        sx={{
          paddingY: "6px"
        }}
      >
        <Box
          sx={{
            marginLeft: "50px"
          }}
        />
        <DesktopHeaderNavUserDropdown label={t("Admin")} />
      </Box>
    );
  } else {
    return (
      <NavItems>
        <FindClassesLink t={t} />
        <FavoritesLink t={t} />
        {isOrgApplicant && <SellerOrgManageLink t={t} />}
        {isLeader && (
          <TeachLink isLeader={isLeader} isSellerOrg={isSellerOrg} t={t} />
        )}
        {showScheduleLink && <ScheduleLink t={t} />}
        {pricingOfferAdminOrgUid && (
          <WalletDashboardLink
            organizationUid={pricingOfferAdminOrgUid}
            t={t}
          />
        )}
        {showWalletFunds && <WalletFundsNavItem />}
        <DesktopHeaderNavUserDropdown />
      </NavItems>
    );
  }
}

/**
 * Header navigation for logged in users when the educator mode feature is enabled.
 * Certain elements are shown only in Educator mode or Parent mode.
 */
function UserModeNav({
  isLeader,
  isSellerOrg,
  showScheduleLink,
  pricingOfferAdminOrgUid,
  showWalletFunds,
  t
}) {
  const locale = useLocale();
  const userMode = useUserMode();

  // A few components are shared between modes.
  const scheduleLink = showScheduleLink && <ScheduleLink t={t} />;

  // Only show the "Switch to ..." button for users that are both teachers and parents.
  if (userMode === UserMode.Educator) {
    return (
      <>
        <EducatorModeToolbar />
        <NavItems>
          {isSellerOrg && <SellerOrgManageLink t={t} />}
          <SwitchModeButton
            userMode={userMode}
            isSellerOrg={isSellerOrg}
            t={t}
          />
          {pricingOfferAdminOrgUid && (
            <WalletDashboardLink
              organizationUid={pricingOfferAdminOrgUid}
              t={t}
            />
          )}
          <DesktopHeaderNavUserDropdown />
        </NavItems>
      </>
    );
  } else {
    return (
      <NavItems>
        <FindClassesLink t={t} />
        <FavoritesLink t={t} />
        {isLeader && (
          <SwitchModeButton
            userMode={userMode}
            isSellerOrg={isSellerOrg}
            t={t}
          />
        )}
        {scheduleLink}
        {pricingOfferAdminOrgUid && (
          <WalletDashboardLink
            organizationUid={pricingOfferAdminOrgUid}
            t={t}
          />
        )}
        {showWalletFunds && <WalletFundsNavItem />}
        <HelpLink hideLabel={locale === BASE_LOCALE} t={t} />
        <DesktopHeaderNavUserDropdown />
      </NavItems>
    );
  }
}

function FavoritesLink({ t }: { t: TFunction }) {
  return (
    <Link to={Routes.savedClassesPath()} trackingName="nav_favorites">
      <HeaderNavItem>
        <HeaderNavLabel icon={faHeart} label={t("Favorites")} />
      </HeaderNavItem>
    </Link>
  );
}

function FindClassesLink({ t }: { t: TFunction }) {
  return (
    <Link to={searchPath()} trackingName="nav_find_classes">
      <HeaderNavItem>
        <HeaderNavLabel icon={faSearch} label={t("Find Classes")} />
      </HeaderNavItem>
    </Link>
  );
}

function TeachLink({
  isLeader,
  isSellerOrg,
  t
}: {
  isLeader: boolean;
  isSellerOrg: boolean;
  t: TFunction;
}) {
  return (
    <Link
      to={
        isSellerOrg
          ? teacherSchedulePath()
          : isLeader
          ? teacherDashboardPath()
          : leadActivitiesPath()
      }
      trackingName="nav_teach_link"
    >
      <HeaderNavItem>
        <HeaderNavLabel icon={faChalkboardTeacher} label={t("Teach")} />
      </HeaderNavItem>
    </Link>
  );
}

function SellerOrgManageLink({ t }: { t: TFunction }) {
  return (
    <Link to={sellerOrgPath()} trackingName="nav_manage_link">
      <HeaderNavItem>
        <HeaderNavLabel icon={faUsersCog} label={t("Manage")} />
      </HeaderNavItem>
    </Link>
  );
}

function HelpLink({
  hideLabel = false,
  t
}: {
  hideLabel: boolean;
  t: TFunction;
}) {
  return (
    <NavHelpLink>
      <HeaderNavItem>
        <HeaderNavLabel
          icon={faQuestionCircle}
          label={t("Help")}
          hideLabel={hideLabel}
        />
      </HeaderNavItem>
    </NavHelpLink>
  );
}

function LoginLink({ t }: { t: TFunction }) {
  return (
    <Link to={Routes.loginPath()} trackingName="nav_login_link">
      <HeaderNavItem>
        <HeaderNavLabel icon={faSignIn} label={t("Log In")} />
      </HeaderNavItem>
    </Link>
  );
}

function ScheduleLink({ t }: { t: TFunction }) {
  return (
    <Link to={Routes.learnSchedulePath()} trackingName="nav_schedule_link">
      <HeaderNavItem>
        <HeaderNavLabel icon={faCalendarAlt} label={t("Schedule")} />
      </HeaderNavItem>
    </Link>
  );
}

function WalletDashboardLink({
  organizationUid,
  t
}: {
  organizationUid: string;
  t: TFunction;
}) {
  return (
    <Link
      to={Routes.pricingOfferAdminDashboardPath(null, organizationUid)}
      trackingName="nav_pricing_offer_dashbaord_topbar"
    >
      <HeaderNavItem>
        <HeaderNavLabel icon={faChartLine} label={t("Dashboard")} />
      </HeaderNavItem>
    </Link>
  );
}

export const PopoverMenu: React.FC<{
  open: boolean;
  setOpen: (open: boolean) => void;
  renderButton: (
    ref: React.Ref<HTMLElement>,
    buttonRef: React.Ref<HTMLElement>
  ) => React.ReactElement;
  buttonRef: React.MutableRefObject<HTMLElement>;
}> = props => {
  const popoverRef = React.useRef<HTMLElement>();

  const hide = (event: Event) => {
    const el = document.getElementById("localization-picker-menu");
    const target = event.target as HTMLButtonElement;
    if (!el?.contains(target)) {
      props.setOpen(false);
    }
  };
  useClickOutsideRefsEventListener(
    document,
    [popoverRef, props.buttonRef],
    hide
  );
  useEventListener(document, "keyup", (event: Event) => {
    if (event["key"] === "Escape") {
      hide(event);
    }
  });

  return (
    <Popover
      ref={popoverRef}
      renderButton={ref => props.renderButton(ref, props.buttonRef)}
      isHidden={!props.open}
    >
      {props.children}
    </Popover>
  );
};

export function DesktopHeaderNavUserDropdown({ label }: { label?: string }) {
  const { t } = useTranslation("client\\components\\nav\\DesktopHeaderNav");
  const [numCloseCount, setNumCloseCount] = React.useState(0);
  const [open, setOpen] = React.useState(false);
  const desktopHeaderNavUserDropdownNode = React.useRef<HTMLElement>();
  const { trackTouch: trackDesktopHeaderNavUserDropdownTouch } =
    useImpressionTracking({
      node: desktopHeaderNavUserDropdownNode.current,
      trackingLabel: "desktop_header_menu_open_button",
      uniqueId: `desktop_header_menu_open_button-${numCloseCount}`,
      sharedProperties: {
        numCloseCount
      }
    });
  const { currentUser } = useSession();

  return (
    <PopoverMenu
      open={open}
      setOpen={(openBool: boolean) => {
        if (!openBool) {
          setNumCloseCount(numCloseCount + 1);
        }
        setOpen(openBool);
      }}
      buttonRef={desktopHeaderNavUserDropdownNode}
      renderButton={(ref, buttonRef) => {
        return (
          <Box ref={buttonRef}>
            <HeaderNavItem
              sx={{ paddingY: "0.5em" }}
              onClick={() => {
                if (open) {
                  setNumCloseCount(numCloseCount + 1);
                } else {
                  trackDesktopHeaderNavUserDropdownTouch();
                }
                setOpen(!open);
              }}
              ref={ref as React.Ref<HTMLDivElement>}
            >
              <UserHeadshotImage size={32} user={currentUser} />
              {label && (
                <Box
                  sx={{
                    marginLeft: "0.5em",
                    color: "grey.500"
                  }}
                >
                  {label}
                </Box>
              )}
              <Icon
                icon={open ? faChevronUp : faChevronDown}
                sx={{ marginLeft: "small" }}
              />
              <span style={visuallyHidden}>{t`Menu`}</span>
            </HeaderNavItem>
          </Box>
        );
      }}
    >
      <HeaderMenu onClose={() => setOpen(false)} />
    </PopoverMenu>
  );
}

export function HeaderNavLabel({
  label,
  icon,
  iconSx = {},
  hideLabel
}: {
  label: string;
  icon?: IconDefinition;
  iconSx?: SystemStyleObject;
  hideLabel?: boolean;
}) {
  return (
    <Box
      flex
      sx={{
        alignItems: "center"
      }}
      aria-label={label}
    >
      {icon && (
        <Icon
          icon={icon}
          size="large"
          sx={{ marginRight: hideLabel ? "none" : "small", ...iconSx }}
        />
      )}
      {!hideLabel && label}
    </Box>
  );
}

function NavItems({ children }: { children: React.ReactNode[] }) {
  return (
    <Box
      flex
      sx={{
        gap: "0.5em",
        paddingY: "6px",

        "a:hover:not(:disabled),a:focus:not(:disabled)": {
          textDecoration: "none"
        }
      }}
    >
      {children}
    </Box>
  );
}

interface SwitchModeButtonProps {
  userMode: UserMode;
  isSellerOrg: boolean;
  t: TFunction;
}

function SwitchModeButton({ userMode, isSellerOrg, t }: SwitchModeButtonProps) {
  return userMode === UserMode.Educator ? (
    <Link to={Routes.parentRootPath()} trackingName="nav_parent_link">
      <HeaderNavItem>{t`Switch to Parent`}</HeaderNavItem>
    </Link>
  ) : (
    <TeachLink isLeader={true} isSellerOrg={isSellerOrg} t={t} />
  );
}
